import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react'
import { LAYERS } from './overlayLayer'

export type ToastTone = 'success' | 'error' | 'info'

export interface ToastOptions {
  /** A button on the toast, for example Undo. Using it also closes the toast. */
  action?: { label: string; onClick: () => void }
  /** How long it stays, in ms. Default: 4 s for success and info; errors stay until dismissed. */
  duration?: number
}

export interface ToastApi {
  success: (text: string, options?: ToastOptions) => string
  error: (text: string, options?: ToastOptions) => string
  info: (text: string, options?: ToastOptions) => string
  /** Closes a toast early, by the id the call above returned. */
  dismiss: (id: string) => void
}

interface ToastEntry {
  id: string
  tone: ToastTone
  text: string
  action?: ToastOptions['action']
  duration: number | null
}

/** Success and info go by themselves after this long. */
export const TOAST_DURATION_MS = 4000
const MAX_TOASTS = 3

const TONES = {
  success: { Icon: CheckCircle2, icon: 'text-success', border: 'border-border' },
  error: { Icon: AlertCircle, icon: 'text-danger', border: 'border-danger/40' },
  info: { Icon: Info, icon: 'text-info', border: 'border-border' },
} as const

// Outside a ToastProvider (a unit test that doesn't care) toasts are dropped.
const dropToasts: ToastApi = { success: () => '', error: () => '', info: () => '', dismiss: () => {} }
const ToastContext = createContext<ToastApi>(dropToasts)

/**
 * `const toast = useToast()`, then `toast.success('Saved.')`, `toast.error(message)`
 * or `toast.info(text, { action: { label: 'Undo', onClick } })`.
 */
export function useToast(): ToastApi {
  return useContext(ToastContext)
}

/**
 * Shows the app's toasts: top right on desktop, top centre on phones (the
 * Assistant button sits bottom right). At most three at a time, newest on top.
 * Mount it once, near the root.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])
  const lastId = useRef(0)

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.filter((t) => t.id !== id))
  }, [])

  const api = useMemo<ToastApi>(() => {
    const show = (tone: ToastTone, text: string, options: ToastOptions = {}) => {
      lastId.current += 1
      const id = `toast-${lastId.current}`
      const toast: ToastEntry = {
        id,
        tone,
        text,
        action: options.action,
        duration: options.duration ?? (tone === 'error' ? null : TOAST_DURATION_MS),
      }
      setToasts((list) =>
        [
          // The same message again replaces the old one (fresh timer) instead of stacking.
          ...list.filter((t) => t.action || toast.action || t.tone !== tone || t.text !== text),
          toast,
        ].slice(-MAX_TOASTS),
      )
      return id
    }
    return {
      success: (text, options) => show('success', text, options),
      error: (text, options) => show('error', text, options),
      info: (text, options) => show('info', text, options),
      dismiss,
    }
  }, [dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed inset-x-0 top-16 flex flex-col items-center gap-2 px-4 sm:items-end sm:px-5" style={{ zIndex: LAYERS.toast }}>
          {[...toasts].reverse().map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

function ToastCard({ toast, onDismiss }: { toast: ToastEntry; onDismiss: (id: string) => void }) {
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const paused = hovered || focused
  // Time left, so a pause resumes where it stopped instead of starting over.
  const left = useRef(toast.duration)

  useEffect(() => {
    if (left.current === null || paused) return
    const startedAt = Date.now()
    const timer = window.setTimeout(() => onDismiss(toast.id), left.current)
    return () => {
      window.clearTimeout(timer)
      if (left.current !== null) left.current = Math.max(0, left.current - (Date.now() - startedAt))
    }
  }, [paused, toast.id, onDismiss])

  const { Icon, icon, border } = TONES[toast.tone]
  return (
    <div
      className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border ${border} bg-surface px-4 py-3 text-foreground shadow-lg motion-safe:animate-fade-in`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false)
      }}
    >
      <Icon size={18} className={`mt-0.5 shrink-0 ${icon}`} aria-hidden />
      <p role={toast.tone === 'error' ? 'alert' : 'status'} className="min-w-0 flex-1 text-sm">
        {toast.text}
      </p>
      {toast.action ? (
        <button
          type="button"
          onClick={() => {
            toast.action?.onClick()
            onDismiss(toast.id)
          }}
          className="shrink-0 rounded text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {toast.action.label}
        </button>
      ) : null}
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
        className="-m-1 shrink-0 rounded-lg p-1 text-foreground-muted hover:bg-surface-hover hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X size={16} aria-hidden />
      </button>
    </div>
  )
}
